/**
 * Os marcos de vocês — a coleção `milestones`.
 *
 * É o `Milestone` de `lib/features/dashboard/domain/models/milestone.dart` com
 * as mesmas chaves, e a ordem é a do `MilestoneService.milestones`: do mais
 * antigo para o mais novo, ordenado aqui na memória porque o armazém entrega
 * por identificador. São dezenas de itens, não milhares.
 *
 * O leitor e o gravador, agora os dois. Durante todo o porte houve só o leitor,
 * porque lá o `toMap` existe para um `addMilestone` que tela nenhuma chama —
 * quem escrevia um marco escrevia fora do app, no console do Firebase. Ficou
 * anotado aqui que o par entraria no dia em que houvesse uma tela de guardar
 * marco; a tela chegou (a folha do `ArquivoVivo`, nos dois apps), então o
 * `marcoParaMapa` entra com ela. É a mesma divergência de sempre: o buraco é do
 * Flutter também, e a casa o fecha do lado de cá.
 */

import { gravarData, lerData } from '../nucleo/tempo';
import type { NomeDoIcone } from '../theme/desenhos';

/** Os cinco do `MilestoneCategory`, na ordem em que o enum os declara. */
export type TipoDeMarco = 'firstDate' | 'trip' | 'achievement' | 'anniversary' | 'memory';

export interface Marco {
  id: string;
  titulo: string;
  descricao: string;
  quando: Date;
  /** Os endereços das fotos. O cartão passa por elas de três em três segundos. */
  imagens: string[];
  /** Quem guardou. Fica `null` quando o documento não diz, como o `String?` de lá. */
  autor: string | null;
  tipo: TipoDeMarco;
}

/**
 * O `displayName` de cada categoria, junto com o traço da casa que a
 * representa.
 *
 * As palavras ficam em inglês porque estão em inglês lá, e o porte não traduz o
 * que o outro app mostra. O `icone` não tem par no Flutter: lá a categoria é um
 * emoji (`💕`, `✈️`, `🏆`, `🎂`, `⭐`), e aqui a casa não põe emoji em tela — o
 * coração do primeiro encontro, o avião da viagem, o troféu da conquista, o
 * brilho do aniversário e a estrela do resto.
 */
export const TIPOS_DE_MARCO: Array<{
  chave: TipoDeMarco;
  icone: NomeDoIcone;
  nome: string;
}> = [
  { chave: 'firstDate', icone: 'coracao', nome: 'First Date' },
  { chave: 'trip', icone: 'aviao', nome: 'Trip' },
  { chave: 'achievement', icone: 'trofeu', nome: 'Achievement' },
  { chave: 'anniversary', icone: 'brilho', nome: 'Anniversary' },
  { chave: 'memory', icone: 'estrela', nome: 'Memory' },
];

const POR_CHAVE = new Map(TIPOS_DE_MARCO.map((t) => [t.chave, t]));

/** O `orElse: () => MilestoneCategory.memory` do `firstWhere` de lá. */
const doTipo = (t: TipoDeMarco) => POR_CHAVE.get(t) ?? TIPOS_DE_MARCO[4];

export const iconeDoMarco = (t: TipoDeMarco): NomeDoIcone => doTipo(t).icone;

function tipoDe(valor: unknown): TipoDeMarco {
  return POR_CHAVE.has(valor as TipoDeMarco) ? (valor as TipoDeMarco) : 'memory';
}

export function marcoDeMapa(id: string, dados: Record<string, unknown>): Marco {
  return {
    id,
    titulo: typeof dados.title === 'string' ? dados.title : '',
    descricao: typeof dados.description === 'string' ? dados.description : '',
    // Data que não se entende vira hoje, como o `?? DateTime.now()` de lá: um
    // marco sem data some da linha do tempo, e sumir é pior do que aparecer no
    // lugar errado.
    quando: lerData(dados.date) ?? new Date(),
    imagens: Array.isArray(dados.imageUrls) ? dados.imageUrls.map((u) => String(u)) : [],
    autor: typeof dados.author === 'string' ? dados.author : null,
    tipo: tipoDe(dados.category),
  };
}

/**
 * O `Milestone.toMap` de lá, com as mesmas chaves que o `marcoDeMapa` lê.
 *
 * O `id` fica de fora porque ele é o nome do documento, e não um campo dentro
 * dele — quem grava é o `armazem.adicionar`, que sorteia o nome. A data sai
 * como o `gravarData` da casa manda, que é o mesmo ISO que o Flutter grava.
 */
export function marcoParaMapa(marco: Omit<Marco, 'id'>): Record<string, unknown> {
  return {
    title: marco.titulo,
    description: marco.descricao,
    date: gravarData(marco.quando),
    imageUrls: marco.imagens,
    author: marco.autor,
    category: marco.tipo,
  };
}

/** Do mais antigo para o mais novo: o `sort` do `MilestoneService.milestones`. */
export function marcosEmOrdem(marcos: Marco[]): Marco[] {
  return [...marcos].sort((a, b) => a.quando.getTime() - b.quando.getTime());
}
